import {
    Component,
    ViewChild,
    inject,
    ChangeDetectionStrategy,
} from '@angular/core';
import { NgTemplateOutlet } from '@angular/common';
import { Router, ActivatedRoute } from '@angular/router';

import {
    AuthService,
    AppInfoService,
    returnUrlAfterLogin,
} from '../../services';
import { DxButtonComponent } from 'devextreme-angular/ui/button';
import { DxCheckBoxComponent } from 'devextreme-angular/ui/check-box';
import { DxTextBoxComponent } from 'devextreme-angular/ui/text-box';
import { DxValidatorComponent } from 'devextreme-angular/ui/validator';
import { DxValidationGroupComponent } from 'devextreme-angular/ui/validation-group';
import { SignInOutput, ConfirmSignInOutput } from 'aws-amplify/auth';
import notify from 'devextreme/ui/notify';
import { DxiValidationRuleComponent } from 'devextreme-angular/ui/nested';
import { DxValidationSummaryComponent } from 'devextreme-angular/ui/validation-summary';

@Component({
    selector: 'app-login-form',
    templateUrl: './login-form.component.html',
    styleUrls: ['./login-form.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        NgTemplateOutlet,
        DxValidationGroupComponent,
        DxTextBoxComponent,
        DxValidatorComponent,
        DxiValidationRuleComponent,
        DxCheckBoxComponent,
        DxValidationSummaryComponent,
        DxButtonComponent,
    ],
})
export class LoginFormComponent {
    private authService = inject(AuthService);
    appInfo = inject(AppInfoService);
    private router = inject(Router);
    private route = inject(ActivatedRoute);

    public LoginUIState: typeof LoginUIState = LoginUIState;
    currentUIState = LoginUIState.SignIn;
    login = '';
    password = '';
    rememberMe = false;
    verificationCode = '';
    newPassword = '';
    confirmNewPassword = '';
    notificationMessage = '';
    // Guards every async auth call so submit buttons can't be double-clicked
    // while a request is in flight.
    isProcessing = false;

    private newPasswordField!: DxTextBoxComponent;
    private confirmNewPasswordField!: DxTextBoxComponent;

    @ViewChild('newPasswordField') set _newPasswordField(
        content: DxTextBoxComponent,
    ) {
        if (content) {
            this.newPasswordField = content;
        }
    }
    @ViewChild('confirmNewPasswordField') set _confirmNewPasswordField(
        content: DxTextBoxComponent,
    ) {
        if (content) {
            this.confirmNewPasswordField = content;
        }
    }

    onLoginClick(args: any) {
        this.clearNotificationMessage();
        if (!args.validationGroup.validate().isValid) {
            return;
        }
        // Choose the token store (local vs session) before signing in so the
        // tokens land in the right place.
        this.authService.setRememberMe(this.rememberMe);
        this.isProcessing = true;
        this.authService
            .logIn(this.login, this.password)
            .then((result: SignInOutput) => {
                if (
                    result.nextStep?.signInStep ===
                    'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED'
                ) {
                    this.currentUIState = LoginUIState.CompleteNewPassword;
                } else if (result.isSignedIn) {
                    args.validationGroup.reset();
                    this.onSignedIn();
                }
            })
            .catch((error) => {
                if (
                    error &&
                    (error.name === 'PasswordResetRequiredException' ||
                        error.code === 'PasswordResetRequiredException')
                ) {
                    this.currentUIState =
                        LoginUIState.RequestForgotPasswordCode;
                } else if (error && error.message) {
                    this.notifyError(error.message);
                }
            })
            .finally(() => {
                this.isProcessing = false;
            });
    }

    requestResetPasswordVerificationCode(username: string) {
        this.clearNotificationMessage();
        this.isProcessing = true;
        this.authService
            .requestResetPasswordVerificationCode(username)
            .then(() => {
                this.currentUIState = LoginUIState.CompleteForgotPassword;
            })
            .catch((error) => {
                // Amplify v6 reports the error type on `name`; keep `code` as a
                // fallback for older error shapes.
                const errorName = error && (error.name || error.code);
                if (errorName === 'UserNotFoundException') {
                    this.notifyError('User you provided does not exist.');
                } else if (error && error.message) {
                    this.notifyError(error.message);
                }
            })
            .finally(() => {
                this.isProcessing = false;
            });
    }

    onResetPasswordClick(args: any) {
        this.clearNotificationMessage();
        if (!args.validationGroup.validate().isValid) {
            return;
        }
        this.isProcessing = true;
        this.authService
            .confirmForgotPassword(
                this.verificationCode,
                this.login,
                this.newPassword,
            )
            .then(() => {
                this.currentUIState = LoginUIState.SignIn;
                notify({
                    message: 'Your password has been reset successfully.',
                    type: 'success',
                    displayTime: 10000,
                    position: 'top center',
                });
            })
            .catch((error) => {
                if (error && error.message) {
                    this.notifyError(error.message);
                }
            })
            .finally(() => {
                this.isProcessing = false;
            });
    }

    completeNewPassword(args: any) {
        this.clearNotificationMessage();
        if (!args.validationGroup.validate().isValid) {
            return;
        }
        this.isProcessing = true;
        this.authService
            .completeNewPassword(this.newPassword)
            .then((result: ConfirmSignInOutput) => {
                if (result.isSignedIn) {
                    this.onSignedIn();
                }
            })
            .catch((error) => {
                if (error && error.message) {
                    this.notifyError(error.message);
                }
            })
            .finally(() => {
                this.isProcessing = false;
            });
    }

    onSendVerificationCodeButtonClicked(args: any) {
        this.clearNotificationMessage();
        if (!args.validationGroup.validate().isValid) {
            return;
        }
        this.requestResetPasswordVerificationCode(this.login);
    }

    onSignedIn() {
        // Back to the page the auth guard bounced from.
        this.router.navigateByUrl(
            returnUrlAfterLogin(
                this.route.snapshot.queryParamMap.get('returnUrl'),
            ),
        );
    }

    onForgotPasswordClicked() {
        this.clearNotificationMessage();
        this.currentUIState = LoginUIState.RequestForgotPasswordCode;
    }

    onBackToSignInClicked() {
        this.clearNotificationMessage();
        this.currentUIState = LoginUIState.SignIn;
    }

    validateConfirmPassword() {
        return this.newPasswordField.text === this.confirmNewPasswordField.text;
    }

    notifyError(errorMessage: string) {
        this.notificationMessage = errorMessage;
    }

    clearNotificationMessage() {
        this.notificationMessage = '';
    }
}

export enum LoginUIState {
    SignIn,
    CompleteNewPassword,
    CompleteForgotPassword,
    RequestForgotPasswordCode,
}
